import { useLayoutEffect, useMemo, useRef, type ReactNode } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import SectionHeader from "@/components/SectionHeader";
import { useMediaQuery } from "@/lib/media";

gsap.registerPlugin(ScrollTrigger);

export const BAND_QUERY = "(min-width: 1024px)";

const ENTRANCES = [
  { y: "-90%", x: -40, rotate: -6, scale: 1 },
  { y: "90%", x: 34, rotate: 7, scale: 1 },
  { y: "0%", x: 0, rotate: -3, scale: 1.6 },
  { y: "-70%", x: 70, rotate: 5, scale: 1 },
  { y: "60%", x: -60, rotate: -9, scale: 0.6 },
  { y: "-100%", x: 0, rotate: 10, scale: 1 },
  { y: "80%", x: 46, rotate: -4, scale: 1.25 },
  { y: "-60%", x: -74, rotate: 8, scale: 0.75 },
];

const BLOCKS = [
  "w-full lg:w-4/6",
  "w-full lg:ml-auto lg:w-4/6 lg:text-right",
  "w-full lg:w-4/6",
];

const COPY = "text-2xl font-light leading-relaxed";
const CALL = "text-xl font-light leading-relaxed";

const LEVELS = [COPY, COPY, CALL];

// o elemento ja passou do ponto de gatilho (topo acima de `ratio` da viewport)
const isPast = (el: Element, ratio: number) =>
  el.getBoundingClientRect().top < window.innerHeight * ratio;

const splitWords = (text: string) =>
  text
    .trim()
    .split(/\s+/)
    .flatMap((word, index) => [
      ...(index > 0 ? [" "] : []),
      <span key={`${word}-${index}`} className="band-word">
        <span className="band-word-inner">{word}</span>
      </span>,
    ]);

export default function AboutBand({
  eyebrow,
  paragraphs,
  centers,
  cta,
}: {
  eyebrow: string;
  paragraphs: string[];
  centers: string;
  cta: ReactNode;
}) {
  const animated = useMediaQuery(BAND_QUERY);

  const sectionRef = useRef<HTMLElement>(null);
  const blockRefs = useRef<(HTMLDivElement | null)[]>([]);
  const ctaRef = useRef<HTMLDivElement>(null);

  const texts = useMemo(
    () => [...paragraphs, centers],
    [paragraphs, centers],
  );

  // o conteudo muda quando o idioma muda, mas a quantidade de blocos nao;
  // sem depender do texto em si a animacao nao reroda e as palavras
  // remontadas ficam com o opacity: 0 do CSS.
  const textKey = texts.join("\u0001");
  const lastIndex = texts.length - 1;

  useLayoutEffect(() => {
    if (!animated) return;

    const context = gsap.context(() => {
      blockRefs.current.forEach((block, index) => {
        if (!block) return;
        const words = block.querySelectorAll<HTMLElement>(".band-word-inner");
        if (!words.length) return;

        gsap.set(words, {
          opacity: 0,
          x: (i: number) => ENTRANCES[i % ENTRANCES.length].x,
          y: (i: number) => ENTRANCES[i % ENTRANCES.length].y,
          rotate: (i: number) => ENTRANCES[i % ENTRANCES.length].rotate,
          scale: (i: number) => ENTRANCES[i % ENTRANCES.length].scale,
        });

        const entrance = {
          opacity: 1,
          x: 0,
          y: 0,
          rotate: 0,
          scale: 1,
          duration: 0.9,
          ease: "power3.out",
          stagger: { each: 0.05, from: "start" as const },
        };

        // um ScrollTrigger criado com o elemento ja dentro do range nao
        // dispara (nao ha transicao de fora para dentro). isso acontece ao
        // trocar de idioma com a secao na tela, entao anima-se direto.
        if (isPast(block, 0.8)) {
          gsap.to(words, entrance);
        } else {
          gsap.to(words, {
            ...entrance,
            scrollTrigger: {
              trigger: block,
              start: "top 80%",
              once: true,
            },
          });
        }

        if (index === lastIndex && ctaRef.current) {
          const cta = { opacity: 0, y: 28, duration: 0.7, ease: "power3.out" };

          if (isPast(ctaRef.current, 0.88)) {
            gsap.from(ctaRef.current, cta);
          } else {
            gsap.from(ctaRef.current, {
              ...cta,
              scrollTrigger: { trigger: ctaRef.current, start: "top 88%", once: true },
            });
          }
        }
      });
    }, sectionRef);

    return () => context.revert();
  }, [animated, textKey, lastIndex]);

  if (!animated) {
    return (
      <section
        id="secao-2"
        className="page-shell text-ink z-2 flex flex-col gap-16 px-4 pt-20 pb-12 md:gap-24 md:px-8 md:pt-24 md:pb-14 xl:px-16"
      >
        <div className="flex flex-col gap-8 md:gap-10">
          <SectionHeader eyebrow={eyebrow} />

          <div className="flex w-full flex-col gap-5">
            {paragraphs.map((paragraph, index) => (
              <p
                key={index}
                className="text-left text-xl font-light leading-relaxed hyphens-auto md:text-2xl"
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        <div className="flex w-full flex-col items-start gap-4">
          <p
            className="w-full text-left text-lg font-light leading-relaxed md:text-xl"
          >
            {centers}
          </p>

          <div className="flex w-full flex-col gap-2">{cta}</div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      id="secao-2"
      className="page-shell relative text-ink z-2 -mb-20 flex flex-col px-4 pt-20 pb-6 md:-mb-24 md:px-8 md:pt-24 md:pb-8 xl:px-16"
    >
      <SectionHeader eyebrow={eyebrow} />

      <div className="relative z-1 mt-5 flex flex-col gap-[14vh]">
        {texts.map((text, index) => (
          <div
            key={index}
            ref={(node) => {
              blockRefs.current[index] = node;
            }}
            className={BLOCKS[Math.min(index, BLOCKS.length - 1)]}
          >
            <p
              className={LEVELS[Math.min(index, LEVELS.length - 1)]}
            >
              {splitWords(text)}
            </p>

            {index === texts.length - 1 && (
              <div
                ref={ctaRef}
                className="mt-6 flex w-full flex-col gap-2 lg:flex-row lg:justify-between"
              >
                {cta}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
